<script setup lang="ts">
import { computed, ref, watch, nextTick } from 'vue';
import ToggleSwitch from 'primevue/toggleswitch';
import Select from 'primevue/select';
import InputText from 'primevue/inputtext';
import Textarea from 'primevue/textarea';
import ProgressSpinner from 'primevue/progressspinner';
import Button from 'primevue/button';
import FileUpload, { type FileUploadUploaderEvent } from 'primevue/fileupload';
import moment from 'moment';
import {
  ButtonType,
  Header,
  CustomButton,
  ArchiveStudyDialog,
  AvatarCropDialog,
  AvatarInput,
  ImportStudyDialog
} from '@/components';
import { useStudyStore } from '@/stores/study';
import { useStudySettingsStore } from '@/stores/studySettings';
import StudySchemaExportDialog from '../study_export/StudySchemaExportDialog.vue';

const studyStore = useStudyStore();

const apiKeyExpirationInMonthsOptions = ref([
  { name: '1 Month', value: 1 },
  { name: '3 Months', value: 3 },
  { name: '6 Months', value: 6 },
  { name: 'Never', value: '' }
]);

const dataViewFilterList = ref([
  { name: 'Cases', value: 'cases'},
  { name: 'Entries', value: 'entries'},
  { name: 'Pipeline', value: 'pipeline'}
]);

const selectedDataViewFilter =
  ref(dataViewFilterList.value.find(filter => filter.value === studyStore.selectedStudy?.flags?.preferences?.dataViewFilter));


const openArchiveStudyDialog = ref(false);
const openNewArchiveStudyDialog = () => {
  openArchiveStudyDialog.value = !openArchiveStudyDialog.value;
};

const openAvatarCropDialog = ref(false);
const openNewAvatarCropDialog = () => {
  openAvatarCropDialog.value = !openAvatarCropDialog.value;
};

const openImportStudyDialog = ref(false);
const handleImportStudyDialog = () => {
  openImportStudyDialog.value = !openImportStudyDialog.value;
};

const showSchemaExportDialog = ref<boolean>(false);
const openCloseExportDialog = () => {
  showSchemaExportDialog.value = !showSchemaExportDialog.value;
};

const studySettingsStore = useStudySettingsStore();
studySettingsStore.getAllData();

watch(
  () => studyStore.selectedStudy,
  (newId, oldId) => {
    if (oldId && newId !== oldId) {
      studySettingsStore.getAllData();
    }
  }
);

const caseManagementChange = computed({
  get: () => studySettingsStore.caseManagement === 'long',
  set: value => {
    studySettingsStore.caseManagement = value ? 'long' : 'cross';
  }
});

const apiKeyExpirationInMonthsChange = computed({
  get: () =>
    apiKeyExpirationInMonthsOptions.value.find(
      option => option.value === studySettingsStore.apiKeyExpirationInMonths
    ) || null,
  set: selectedOption => {
    studySettingsStore.apiKeyExpirationInMonths =
      selectedOption?.value as number;
  }
});

const apiKeyNeverExpire = computed(() => {
  const apiKeyExpiration = moment(
    studySettingsStore.investigation.apiKeyExpiration
  );
  const year = moment().add(1, 'year');
  return apiKeyExpiration.isSameOrAfter(year);
});

const patchDetails = async () => {
  await studySettingsStore.patchDetails(
    {
      title: studySettingsStore.investigation.title,
      type: studySettingsStore.caseManagement,
      webhooks: studySettingsStore.webhooks
    },
    studySettingsStore.loaders.investigation
  );
  formModified.value = false;
};
const formModified = ref(false);

const onFormChange = () => {
  formModified.value = true;
};
const copyApiKeyToClipboard = () =>
  navigator.clipboard.writeText(studySettingsStore.investigation.apiKey);

const fileupload = ref();
const onImportFileSelected = async (event: FileUploadUploaderEvent) => {
  await studyStore.importAndValidateStudy((event.files as File[])[0]);
  if (studyStore.studyImportDiff) {
    handleImportStudyDialog();
  }

  // resetting fileupload input
  fileupload.value.clear();
  await nextTick(() => {
    fileupload.value.$params.state.files = [];
    fileupload.value.$params.state.uploadedFileCount = 0;
  });
};

const studyPreferences = ref(studyStore.selectedStudy?.flags?.preferences);

const initialEntryMetrics = ref(studyStore.selectedStudy?.flags?.enableEntryMetrics ?? false);
const currentEntryMetrics = ref(studyStore.selectedStudy?.flags?.enableEntryMetrics ?? false);

const initialClosedFormMessage = ref(studyStore.selectedStudy?.flags?.closedFormMessage ?? '');
const closedFormMessage = ref(studyStore.selectedStudy?.flags?.closedFormMessage ?? '');

watch(() => studyStore.selectedStudy?.identifier, () => {
  initialEntryMetrics.value = studyStore.selectedStudy?.flags?.enableEntryMetrics ?? false;
  currentEntryMetrics.value = studyStore.selectedStudy?.flags?.enableEntryMetrics ?? false;
  initialClosedFormMessage.value = studyStore.selectedStudy?.flags?.closedFormMessage ?? '';
  closedFormMessage.value = studyStore.selectedStudy?.flags?.closedFormMessage ?? '';
});

const enableEntryMetrics = computed({
  get: () => currentEntryMetrics.value,
  set: value => {
    currentEntryMetrics.value = value;
    if (!studyStore.selectedStudy?.flags) {
      studyStore.selectedStudy!.flags = {
        spRedirect: false,
        importExportStudy: false
      };
    }
    studyStore.selectedStudy!.flags.enableEntryMetrics = value;
  }
});

const isPreferencesPristine = computed(() => {
  const dataViewFilterMatch = selectedDataViewFilter.value?.value === studyPreferences.value?.dataViewFilter;
  const entryMetricsMatch = currentEntryMetrics.value === initialEntryMetrics.value;
  const closedFormMessageMatch = closedFormMessage.value === initialClosedFormMessage.value;
  return dataViewFilterMatch && entryMetricsMatch && closedFormMessageMatch;
});

const saveStudyPreferences = () => {
  // Ensure flags and preferences objects exist
  if (!studyStore.selectedStudy?.flags) {
    // set default flags if they don't exist
    studyStore.selectedStudy!.flags = {
      spRedirect: false,
      importExportStudy: false
    };
  }
  // Ensure preferences object exists
  if (!studyStore.selectedStudy!.flags?.preferences) {
    studyStore.selectedStudy!.flags.preferences = {};
  }
  // Set preferences
  studyStore.selectedStudy!.flags!.preferences.dataViewFilter = selectedDataViewFilter.value?.value;
  studyStore.selectedStudy!.flags!.enableEntryMetrics = currentEntryMetrics.value;
  // Send an empty string (not undefined) when cleared: undefined is dropped
  // during JSON serialization, so the backend's Object.assign merge would keep
  // the old value. An empty string overwrites it and falls back to the default.
  studyStore.selectedStudy!.flags!.closedFormMessage = closedFormMessage.value?.trim() ?? '';
  studySettingsStore.updateStudyFlags();
  initialEntryMetrics.value = currentEntryMetrics.value;
  initialClosedFormMessage.value = closedFormMessage.value;
};
</script>

<template>
  <div class="study-settings">
    <div class="header">
      <Header title="Study Settings"></Header>
    </div>
    <div class="study-settings-container">
      <div v-if="studySettingsStore.isInitialLoadLoading"
        class="progress-container">
        <ProgressSpinner style="width: 32px" strokeWidth="4" />
      </div>
      <template v-if="!studySettingsStore.isInitialLoadLoading">
        <h3 class="sub-header">Basic Config</h3>
        <div class="main">
          <div class="basic-config">
            <div class="left">
              <div class="study-name">
              <div class="study-name-label">Study Name</div>
              <InputText v-model="studySettingsStore.investigation.title"
                @input="onFormChange"
                class="study-name-input"
                placeholder="Enter the name of your study"/>
              <div class="study-name-error">
                <span v-if="studySettingsStore.investigation.title === ''"
                  severity="error">Invalid study name</span>
              </div>
            </div>
            <div class="case-management">
              <div class="case-manage-label">Case Management</div>
              <div class="case-manage-description">
                Cases act as folders and they help you organize data entries
              </div>
              <div class="case-management-bar">
                <div class="switch-container">
                  <ToggleSwitch v-model="caseManagementChange"
                    @input="onFormChange"/>
                  <label class="switch-label"
                    v-if="studySettingsStore.caseManagement === 'long'">ON</label>
                  <label class="switch-label"
                    v-if="studySettingsStore.caseManagement !== 'long'">OFF</label>
                </div>

                <div class="case-management-change-text">
                  {{
                    studySettingsStore.caseManagement === "cross"
                      ? "Turn case management OFF when collecting self-reported data (e.g. patient-reported outcomes)."
                      : "Create cases and share them with your team."
                  }}
                </div>
              </div>
            </div>
            </div>

            <div class="side">
              <div class="study-avatar">
              <b>Study Avatar</b>
              <img alt="Study Avatar"
                :src="'data:image/jpeg;base64,' + studySettingsStore.avatar"/>
              <AvatarInput :openAvatarCropDialog="openAvatarCropDialog"
                @update:openAvatarCropDialog="openAvatarCropDialog = $event"/>
            </div>
            </div>
          </div>

          <div class="webhooks">
            <div class="webhook-label">Webhooks</div>
            <div class="webhook-description">
              Webhooks allow you to integrate Studypages Data with other
              services
            </div>
            <div class="entry-created">
              <div class="header-small">Entry Created</div>
              <div class="entry-created-input-switch">
                <div class="switch-container">
                  <ToggleSwitch v-model="
                      studySettingsStore.webhooks.RESPONSE_CREATED.enabled
                    "
                    @input="onFormChange"/>
                  <label class="switch-label"
                    v-if="studySettingsStore.webhooks.RESPONSE_CREATED.enabled">ON</label>
                  <label class="switch-label"
                    v-if="!studySettingsStore.webhooks.RESPONSE_CREATED.enabled">OFF</label>
                </div>
                <InputText v-model="studySettingsStore.webhooks.RESPONSE_CREATED.url"
                  @input="onFormChange"
                  class="entry-created-webhook-inpit"
                  placeholder="https://example.com/webhook"/>
              </div>
            </div>
            <div class="entry-updated">
              <div class="header-small">Entry Updated</div>
              <div class="entry-created-input-switch">
                <div class="switch-container">
                  <ToggleSwitch v-model="
                      studySettingsStore.webhooks.RESPONSE_UPDATED.enabled
                    "
                    @input="onFormChange"/>
                  <label class="switch-label"
                    v-if="studySettingsStore.webhooks.RESPONSE_UPDATED.enabled">ON</label>
                  <label class="switch-label"
                    v-if="!studySettingsStore.webhooks.RESPONSE_UPDATED.enabled">OFF</label>
                </div>
                <InputText v-model="studySettingsStore.webhooks.RESPONSE_UPDATED.url"
                  @input="onFormChange"
                  class="entry-created-webhook-inpit"
                  placeholder="https://example.com/webhook"/>
              </div>
            </div>
          </div>
          <div class="buttons">
            <CustomButton text="Cancel" />
            <CustomButton v-if="!formModified"
              text="Save"
              :disabled="studySettingsStore.investigation.title.trim() === ''"
              :type="ButtonType.OUTLINED"/>
            <CustomButton v-else
              text="Save"
              :disabled="studySettingsStore.investigation.title.trim() === ''"
              @onClick="patchDetails"
              :type="ButtonType.POSITIVE"
              :loading="studySettingsStore.isInvestigationLoading"/>
          </div>
        </div>

        <h3 class="sub-header second-header">Advance Config</h3>
        <div class="bottom">
          <div class="archive-study">
            <b>Archive Study</b>
            <div class="description">
              You may archive this study so it does not count against your
              billing quota.
            </div>

            <Button v-if="
                studySettingsStore.investigation.archivedAt &&
                !studySettingsStore.billing!.manuallyBilled &&
                studySettingsStore.billing!.activeInvestigations.length >=
                  studySettingsStore.billing!.quotas.plan.studies &&
                studySettingsStore.archive === 'Archive'
              "
              label="Unarchive"
              v-tooltip="
                `You can\'t unarchive this study because you have reached your maximum quota for active studies.
                To unarchive, please first archive other studies and try again`
              "/>
            <CustomButton v-else
              :text="
                studySettingsStore.archive === 'Archive'
                  ? 'Unarchive'
                  : 'Archive'
              "
              :type="ButtonType.OUTLINED"
              @onClick="openNewArchiveStudyDialog"
              :loading="studySettingsStore.isArchiveStudyLoading"/>
          </div>
          <div class="api-key">
            <b>API Key</b>
            <div class="description">
              API key allows you to integrate this study with other services
            </div>
            <div class="api-key-bar">
              <InputText :value="
                  studySettingsStore.investigation.apiKey.toString().length ===
                  4
                    ? '*****************************************' +
                      studySettingsStore.investigation.apiKey
                    : studySettingsStore.investigation.apiKey
                "
                readonly/>
              <CustomButton class="clipboard"
                text=""
                :icon="{ icon: 'clipboard', style: 'light' }"
                @onClick="copyApiKeyToClipboard"/>
              <Select :options="apiKeyExpirationInMonthsOptions"
                v-model="apiKeyExpirationInMonthsChange"
                optionLabel="name"
                placeholder="Select Key Expiration"/>
              <!-- <Button label="Generate API Key" @click="studySettingsStore.patchApiKey"></Button> -->
              <CustomButton text="Generate API Key"
                @onClick="studySettingsStore.patchApiKey"
                :type="ButtonType.OUTLINED"
                :loading="studySettingsStore.isApiKeyLoading"/>
            </div>
            <div v-if="studySettingsStore.investigation.apiKey == ''"
              class="description-small">
              This study doesn't have an API key yet, Generate a new API key to
              get started.
            </div>
            <div v-else-if="apiKeyNeverExpire" class="description-small">
              This API key will never expire, Generating a new API key will
              invalidate existing key for this study.
            </div>
            <div v-else-if="studySettingsStore.investigation.apiKey"
              class="description-small">
              This API key will expire on
              {{
                moment(
                  studySettingsStore.investigation.apiKeyExpiration
                ).format("DD MMM YYYY")
              }}, Generating a new API key will invalidate existing key for this
              study.
            </div>
          </div>

          <div class="study-import-export"
            v-if="studyStore.selectedStudy?.flags?.importExportStudy">
            <b>Import & Export</b>
            <div class="description">
              You may import and export to this study to configure it
            </div>

            <div class="import-export-buttons">
              <CustomButton text="Export"
                :type="ButtonType.OUTLINED"
                @onClick="openCloseExportDialog"/>
              <FileUpload ref="fileupload"
                mode="basic"
                @uploader="onImportFileSelected"
                :auto="true"
                :custom-upload="true"
                :fileLimit="1"
                accept=".xlsx"
                chooseLabel="Import"/>
              <div class="import-export-progress-spinner"
                v-if="studyStore.importExportLoader.isLoading">
                <ProgressSpinner style="width: 32px; height: 32px"
                  strokeWidth="4"/>
              </div>
            </div>
          </div>
        </div>

        <h3 class="sub-header second-header">Preferences</h3>
        <div class="preferences">
          <div class="data-view-filter">
            <h4 class="sub-header">Select DataView Filter</h4>
            <Select v-model="selectedDataViewFilter"
              class="every-select"
              optionLabel="name"
              placeholder="Select option"
              :options="dataViewFilterList"/>
          </div>

          <div class="entry-metrics-toggle">
            <h4 class="sub-header">Entry Metrics</h4>
            <div class="toggle-container">
              <label class="toggle-label">Enable entry metrics tracking</label>
              <ToggleSwitch v-model="enableEntryMetrics" @update:modelValue="onFormChange"/>
            </div>
            <div class="description">
              Track and display average time spent on form entries
            </div>
          </div>

          <div class="closed-form-message">
            <h4 class="sub-header">Closed Data Entry Message</h4>
            <Textarea v-model="closedFormMessage"
              class="closed-form-message-input"
              rows="3"
              cols="5"
              placeholder="The study is completed. Data entry is closed for this form."/>
            <div class="description">
              Shown to everyone when a form has data entry closed. Leave empty to use the default message.
            </div>
          </div>

          <div class="preferences-buttons">
            <CustomButton text="Save"
              :disabled="isPreferencesPristine"
              @onClick="saveStudyPreferences"
              :type="ButtonType.POSITIVE"/>
          </div>
        </div>
      </template>
    </div>
  </div>
  <ArchiveStudyDialog :visible="openArchiveStudyDialog"
    @onClose="openNewArchiveStudyDialog"/>
  <AvatarCropDialog :visible="openAvatarCropDialog"
    @onClose="openNewAvatarCropDialog"/>
  <ImportStudyDialog :visible="openImportStudyDialog"
    @onClose="handleImportStudyDialog"/>
  <StudySchemaExportDialog v-if="showSchemaExportDialog"
    :visible="showSchemaExportDialog"
    @onClose="openCloseExportDialog"/>
</template>

<style scoped lang="scss">
@import "../../assets/variables.module";
.study-settings {
  padding: 48px 100px 40px 80px;
}
.study-settings-container {
  b {
    margin-bottom: 10px;
    font-weight: bold;
    font-size: 16px;
  }
  .header-small {
    font-weight: bold;
  }
  .description-small {
    font-size: 12px;
    font-style: italic;
    color: #6c757d;
  }
  .progress-container {
    display: flex;
    align-items: center;
    grid-column: 1 / -1;
  }
}
.basic-config {
  display: flex;
  justify-content: space-between;

  .side {
    display: flex;
    flex-direction: column;
    flex: 1;
    align-items: center;
    max-width: 250px;

    img {
      min-width: 140px;
    }
  }
}
.sub-header {
  font-weight: 700;
  margin-bottom: 16px;
}
.second-header {
  margin-top: 48px;
}
.main, .bottom, .preferences {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.entry-metrics-toggle {
  .toggle-container {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 8px;
  }
  .toggle-label {
    font-weight: 500;
  }
}

.data-view-filter {
  margin-bottom: 16px;
}

.closed-form-message {
  margin-top: 16px;
  .closed-form-message-input {
    width: 100%;
    margin-bottom: 8px;
    resize: none;
    font-size: 14px;
  }
}

.main, .bottom, .preferences {
  color: #272a2e;
  line-height: 20px;
  font-size: 14px;
  max-width: 900px;
  min-width: 600px;
  padding: 40px;
  border-radius: 4px;
  background-color: #fff;
  box-shadow: 0 0 0 1px #ebeff2, 0 1px 10px 1px rgba(15, 15, 15, 0.05),
    0 1px 3px 1px rgba(15, 15, 15, 0.05);
}
.preferences-buttons {
  display: flex;
  align-items: center;
  margin-top: 16px;
  justify-content: flex-end;
}
.bottom {
  grid-area: bottom;
}
.study-name,
.case-management,
.case-management-change-text,
.archive-study,
.webhooks,
.api-key {
  display: flex;
  flex-direction: column;
  margin: 10px 0;
}
.study-name {
  margin: unset;

  input {
    max-width: 300px;
  }
}
.case-management {
  padding: 48px 0 60px 0;
}
.study-name-label,
.case-manage-label,
.webhook-label {
  font-size: 16px;
  font-weight: 700;
  line-height: 30px;
  padding-bottom: 12px;
}
.study-name-input {
  font-size: 14px;
}
.case-manage-label,
.webhook-label {
  padding: unset;
}
.case-manage-description,
.webhook-description {
  color: #575e66;
  font-size: 14px;
  line-height: 20px;
  margin-bottom: 20px;
}
.switch-label {
  margin-left: 12px;
}
.entry-created,
.entry-updated {
  .header-small {
    line-height: 30px;
  }

  .entry-created-input-switch {
    display: flex;
    align-items: center;

    .switch-container {
      margin-right: 32px;
    }

    .entry-created-webhook-inpit {
      font-size: 14px;
      height: 40px;
    }
  }
}
.entry-updated {
  margin-top: 20px;
}
.archive-study {
  padding: 0px 0px 60px 0px;
}
.case-management-change-text {
  text-align: left;
  justify-content: center;
  min-height: 28px;
  max-width: 400px;
  line-height: 14px;
}
.study-avatar {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
}
.study-avatar img {
  width: 50%;
  height: auto;
  border-radius: 10%;
  border: 2px solid #ccc;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}
.study-name input,
.entry-created input,
.entry-updated input,
.api-key input {
  width: 100%;
  font-size: 14px;
}
.case-management-bar,
.api-key-bar {
  display: flex;
  align-items: center;
  text-align: center;
  flex-direction: row;
  gap: 10px;
}
.buttons {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 20px;
}
.archive-study :deep(button),
.study-avatar :deep(button) {
  margin: 8px 0;
  line-height: 16px;
}
.api-key :deep(button) {
  line-height: 20px;
  min-width: 161px;
  border: 1px solid #ced4da;
  background: #ffffff;
  color: #6c757d;
}
.archive-study Button {
  line-height: 20px;
  min-width: 100px;
  border: 1px solid #ced4da;
  background: #ffffff;
  color: #6c757d;
  max-width: 100px;
}
.clipboard {
  :deep(button) {
    min-width: 20px !important;
  }
  :deep(.icon) {
    margin-right: 0;
    height: 16px;
  }
}
.api-key-bar {
  button:hover {
    background-color: hsla(0, 0%, 0%, 0.15);
  }

  :deep(button):hover {
    background-color: hsla(0, 0%, 0%, 0.15);
  }
}
.switch-container {
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #ced4da;
  border-radius: 4px;
  padding: 12px 10px;
  box-sizing: border-box;
}
.study-name-error {
  display: flex;
  justify-content: flex-start;
  span {
    color: red;
  }
}
.study-import-export {
  padding: 48px 0px 0px 0px;

  .description {
    margin-top: 8px;
  }

  .import-export-buttons {
    padding: 16px 0px 0px 0px;
    display: flex;

    .import-export-progress-spinner {
      margin-left: 16px;
      display: flex;
      align-items: center;
    }
  }

  :deep(.p-icon) {
    display: none;
  }

  :deep(.p-button) {
    color: unset;
    background: unset;
    background-color: transparent;
    font-size: unset;
    border-radius: 4px;
    border: 1px solid hsla(0, 0%, 0%, 0.25);
    padding: 9px 12px;
    font-family: unset;
    font-size: 14px;
    margin-left: 16px;

    .p-button-label {
      font-weight: 400;
    }

    &:hover {
      background-color: $disabled;
      border: 1px solid hsla(0, 0%, 0%, 0.25);
      color: unset;
    }
  }

  :deep(.p-fileupload > span) {
    display: none;
  }
}
</style>
